import React, { useEffect, useRef, useState } from "react"
import {
  Input,
  Select,
  Selection,
  Button,
  Text,
  Heading,
  Box,
  Flex,
  Loader,
  Alert,
} from "@singita/components"
import {
  Dates,
  NumberField,
  Radio,
  Lodges,
  Winnie,
  PhoneNumber,
  Upload,
} from "./components"
import { getFormLayoutStyles, getLabel } from "./utils/helpers"
import { Translation } from "react-i18next"

const RECAPTCHA_SITE_KEY = process.env.GATSBY_RECAPTCHA_SITE_KEY || ""

const FormBuilder = ({
  reference,
  fields,
  formId,
  onSubmit,
  renderTitle,
  onChange,
  errors,
  formStyles,
  buttonText = "Submit",
  form,
  setForm,
  isLoading,
}) => {
  const recaptchaRef = useRef(null)
  const [recaptchaError, setRecaptchaError] = useState(null)
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false)
  const [isRecaptchaInitialized, setIsRecaptchaInitialized] = useState(false)

  const initializeRecaptcha = () => {
    if (isRecaptchaInitialized || document.querySelector("#recaptcha-script")) return;
  
    const scriptElement = document.createElement("script");
    if (!RECAPTCHA_SITE_KEY) {
      console.error("Missing RECAPTCHA_SITE_KEY");
      return;
    }
    scriptElement.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    scriptElement.async = true;
    scriptElement.id = "recaptcha-script";
  
    scriptElement.onload = () => {
      setIsRecaptchaLoaded(true);
      setRecaptchaError(null);
      setIsRecaptchaInitialized(true);
    };
  
    scriptElement.onerror = () => {
      console.error("Failed to load reCAPTCHA script.");
      setRecaptchaError("Failed to load reCAPTCHA.");
    };
  
    document.body.appendChild(scriptElement);
  };  

  useEffect(() => {
    if (form.email) {
      initializeRecaptcha()
    }
  }, [form])

  const executeRecaptcha = async () => {
    try {
      if (!window.grecaptcha || !window.grecaptcha.enterprise) {
        console.error("reCAPTCHA is not available.");
        return null;
      }
      
      const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {
        action: `submit_${formId.replace(/[^A-Za-z/_]/g, '')}`,
      });
  
      if (!token) {
        throw new Error("No token received from reCAPTCHA.");
      }
  
      return token;
    } catch (error) {
      console.error("reCAPTCHA execution error:", error);
      setRecaptchaError("Failed to verify. Please try again.");
      return null;
    }
  };
  
  

  const handleSubmit = async e => {
    e.preventDefault()

    setRecaptchaError(null)

    if (!isRecaptchaLoaded) {
      setRecaptchaError("Please wait for reCAPTCHA to load.")
      return
    }

    try {
      const token = await executeRecaptcha()
      await onSubmit({
        recaptchaToken: token,
        recaptchaAction: `submit_${formId.replace(/[^A-Za-z/_]/g, '')}`,
      })
    } catch (error) {
      setRecaptchaError(error.message)
      console.error("reCAPTCHA error:", error)
    }
  }

  return (
    <Box>
      {renderTitle ? renderTitle() : null}
      <Flex as="form" onSubmit={handleSubmit} ref={reference} {...formStyles}>
        {fields.map(
          ({
            name,
            type,
            label,
            options,
            styles,
            placeholder = "",
            variant = "standard",
            size,
            disabled,
            min,
            max,
            orientation,
            group = [],
            initialValue,
            bg,
            required,
            description,
          }) => {
            const layoutStyles = getFormLayoutStyles(styles, variant)
            const realLabel = getLabel(label, required)

            switch (type) {
              case "multiSelect":
                const value = form[name]
                  ? form[name].reduce((result, item) => {
                      const getOption = options.find(o => o.value === item)
                      if (getOption) {
                        result.push({ ...getOption, key: getOption.value })
                      }
                      return result
                    }, [])
                  : []

                return (
                  <Box {...layoutStyles}>
                    <Select
                      id={name}
                      label={realLabel}
                      name={name}
                      options={options.map(option => {
                        return {
                          ...option,
                          key: option.value,
                        }
                      })}
                      onChange={option => {
                        if (option && option.length) {
                          const optionArray = option.map(o => o.value)
                          setForm({ ...form, [name]: optionArray })
                        } else {
                          setForm({ ...form, [name]: [] })
                        }
                      }}
                      placeholder={placeholder}
                      bg="white"
                      noOptionsMessage="No further options"
                      error={errors[name]}
                      isMulti={true}
                      value={value}
                      disabled={isLoading}
                    />
                  </Box>
                )
              case "select":
                return (
                  <Box {...layoutStyles}>
                    <Select
                      label={realLabel}
                      placeholder={placeholder}
                      options={options.map(option => {
                        return {
                          ...option,
                          key: option.value,
                        }
                      })}
                      onChange={option => {
                        setForm({ ...form, [name]: option.value })
                      }}
                      name={name}
                      error={errors[name]}
                      bg="white"
                      value={options.find(
                        option => option.value === form[name]
                      )}
                      disabled={isLoading}
                    />
                    {description && <Text size="tiny">{description}</Text>}
                  </Box>
                )
              case "phone":
                return (
                  <Box {...layoutStyles}>
                    <PhoneNumber
                      form={form}
                      setForm={setForm}
                      name={name}
                      label={realLabel}
                      error={errors[name]}
                      bg={bg}
                      disabled={isLoading}
                    />
                  </Box>
                )
              case "input":
                return (
                  <Box {...layoutStyles}>
                    <Input
                      variant={variant}
                      size="standard"
                      name={name}
                      label={realLabel}
                      type="input"
                      bg="white"
                      onChange={onChange}
                      error={errors[name]}
                      value={form[name] || ""}
                      autoComplete="on"
                      disabled={isLoading || disabled}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "email":
                return (
                  <Box {...layoutStyles}>
                    <Input
                      variant={variant}
                      size="standard"
                      name={name}
                      label={realLabel}
                      type="input"
                      bg="white"
                      onChange={onChange}
                      error={errors[name]}
                      value={form[name] || ""}
                      autoComplete="on"
                      disabled={isLoading}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "textarea":
                return (
                  <Box {...layoutStyles}>
                    <Input
                      variant={variant}
                      size="standard"
                      name={name}
                      label={realLabel}
                      type="textarea"
                      bg="white"
                      onChange={onChange}
                      error={errors[name]}
                      value={form[name] || ""}
                      disabled={isLoading}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "checkbox":
                return (
                  <Box {...layoutStyles}>
                    <Selection
                      label={realLabel}
                      orientation="left"
                      isSpaced={false}
                      onChange={onChange}
                      name={name}
                      error={errors[name]}
                      checked={form[name] || false}
                      value={form[name] || ""}
                      disabled={isLoading}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "radio":
                return (
                  <Box {...layoutStyles}>
                    <Radio
                      label={realLabel}
                      group={group}
                      onChange={onChange}
                      name={name}
                      error={errors[name]}
                      form={form}
                      disabled={isLoading}
                    />
                  </Box>
                )
              case "text":
                return (
                  <Box {...layoutStyles}>
                    <Text size={size}>{label}</Text>
                  </Box>
                )
              case "heading":
                return (
                  <Box {...layoutStyles}>
                    <Heading size={size}>{label}</Heading>
                  </Box>
                )
              case "upload":
                return (
                  <Box {...layoutStyles}>
                    <Upload
                      name={name}
                      placeholder={placeholder}
                      setForm={setForm}
                      form={form}
                      label={realLabel}
                      error={errors[name]}
                      disabled={isLoading}
                    />
                  </Box>
                )
              case "number":
                return (
                  <Box {...layoutStyles}>
                    <NumberField
                      name={name}
                      label={realLabel}
                      disabled={disabled || isLoading}
                      initialValue={initialValue}
                      min={min}
                      max={max}
                      orientation={orientation}
                      form={form}
                      setForm={setForm}
                    />
                  </Box>
                )
              case "date":
                return (
                  <Box {...layoutStyles}>
                    <Dates
                      form={form}
                      setForm={setForm}
                      label={label}
                      name={name}
                      disabled={isLoading}
                    />
                  </Box>
                )
              case "lodges":
                return (
                  <Box {...layoutStyles}>
                    <Lodges
                      label={realLabel}
                      placeholder={placeholder}
                      name={name}
                      error={errors[name]}
                      form={form}
                      setForm={setForm}
                      disabled={isLoading}
                    />
                  </Box>
                )
              default:
                return null
            }
          }
        )}
        <Winnie onChange={onChange} />

        {/* Invisible reCAPTCHA element */}
        <div id="recaptcha" ref={recaptchaRef}></div>

        {recaptchaError && (
          <Alert variant="standard">
            <Text>{recaptchaError}</Text>
          </Alert>
        )}

        <Flex>
          {isLoading ? (
            <Loader isActive={isLoading} size={100} />
          ) : (
            <Button type="submit" disabled={!isRecaptchaLoaded || isLoading}>
              {buttonText}
            </Button>
          )}
        </Flex>
      </Flex>
      {Object.keys(errors).length ? (
        <Flex>
          <Box mt={[1]}>
            <Text color="statusError" size="small">
              <Translation>{t => t("form.required-fields")}</Translation>
            </Text>
          </Box>
        </Flex>
      ) : null}
      <Box mt={4} textAlign="left">
        <Text size="tiny" color="gray.500">
          This form is protected by reCAPTCHA Enterprise and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Terms of Service
          </a>{" "}
          apply.
        </Text>
      </Box>
    </Box>
  )
}

export default FormBuilder
